import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  FormControlLabel,
  Grid,
  Pagination,
  Switch,
  Tooltip,
  Typography,
} from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import { useTranslation } from "react-i18next";
import ComponentHeaderTwo from "common/ComponentHeaderTwo/ComponentHeaderTwo";
import { ReactComponent as Accounts } from "../../assets/SideBarIcons/accounts.svg";
import AccountSettingsTabs from "./AccountSettingsTabs";
import SimpleModal from "common/Form/Form";
import StyledInput from "common/SearchField/Search";
import DynamicSelect from "common/Select/Select";
import { useEffect, useMemo, useState } from "react";
import {
  useGetEventTypesByParentIdService,
  useGetNotificationEventTypesService,
  useGetNotificationSubEventTypesService,
} from "subModule/src/services/accountsettings/useAccountSettings";
import { themes } from "theme/themes";
import { useSelector } from "react-redux";
import { brandingInfo } from "features/server/brandingSlice";
import { AccountSettingsProfileService } from "subModule/src/core/services";
import {
  ControlMultiNotificationResponse,
  ControlNotificationResponse,
  NotificationEventSubTypeResponse,
  NotificationEventTypeResponse,
} from "subModule/src/services/accountsettings/interface";
import AlertMessages from "common/Alert/AlertMessages";
import { useSnackbar } from "notistack";
import Loader from "layout/Loader";

const EVENT_TYPES_PAGE_SIZE = 10;
const SWITCH_WIDTH = 60;
const SWITCH_THUMB_SIZE = 17;
const SWITCH_CHECKED_TRANSLATE_X = SWITCH_WIDTH - SWITCH_THUMB_SIZE - 4;
const SWITCH_COLUMN_WIDTH = 110;
const NOTIFICATION_COLUMNS_WIDTH = SWITCH_COLUMN_WIDTH * 3;
const CHILD_ACCORDION_EXPAND_OFFSET = 40;
const FILTER_CONTROL_HEIGHT = 38;
const FILTER_LABEL_SPACER_HEIGHT = 20;

const filterFieldLabelSpacer = (
  <Typography
    className="font-Saira font-[600] text-[13px]"
    sx={{
      visibility: "hidden",
      lineHeight: "20px",
      height: FILTER_LABEL_SPACER_HEIGHT,
      mb: "2px",
    }}
    aria-hidden
  >
    .
  </Typography>
);

const accordionExpandIconSx = {
  "& .MuiAccordionSummary-expandIconWrapper": {
    transform: "none !important",
    marginRight: "8px",
    marginLeft: 0,
  },
  "&.Mui-expanded .MuiAccordionSummary-expandIconWrapper": {
    transform: "none !important",
  },
};

const AccordionExpandIcon = ({ expanded }: { expanded: boolean }) =>
  expanded ? (
    <ExpandMoreIcon sx={{ color: "#4A4A4A", fontSize: 21 }} />
  ) : (
    <ChevronRightIcon sx={{ color: "#4A4A4A", fontSize: 21 }} />
  );

type NotificationControlValues = {
  inApp: boolean;
  push: boolean;
  broadcast: boolean;
};

const extractNotificationControlValues = (item: {
  notificationControlResponse?: NotificationEventTypeResponse["notificationControlResponse"];
  inApp?: boolean;
  push?: boolean;
  broadcast?: boolean;
}): NotificationControlValues => {
  const control = item.notificationControlResponse || {};
  return {
    inApp: control.inApp ?? item.inApp ?? false,
    push: control.push ?? item.push ?? false,
    broadcast: control.broadcast ?? item.broadcast ?? false,
  };
};

const getAllowedFromSubEvents = (
  items: NotificationEventSubTypeResponse[],
) => ({
  inAppAllowed: items.some(
    (item) => item.active !== false && item.inAppAllowed !== false,
  ),
  pushAllowed: items.some(
    (item) => item.active !== false && item.pushAllowed !== false,
  ),
  broadcastAllowed: items.some(
    (item) => item.active !== false && item.broadcastAllowed !== false,
  ),
});

export default function NotificationSettings() {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [pageNo, setPageNo] = useState<number>(1);
  const [searchEventType, setSearchEventType] = useState<string>("");
  const [searchInputKey, setSearchInputKey] = useState(0);
  const [debouncedSearchTitle, setDebouncedSearchTitle] = useState<string>("");
  const [appliedActive, setAppliedActive] = useState<boolean>(true);
  const statusOptions = useMemo(
    () => [
      { value: true, label: t("active") },
      { value: false, label: t("inactive") },
    ],
    [t],
  );
  const [statusFilter, setStatusFilter] = useState(statusOptions[0]);
  const [expandedParentId, setExpandedParentId] = useState<number | null>(null);
  const [expandedChildId, setExpandedChildId] = useState<number | null>(null);
  const [childControlStates, setChildControlStates] = useState<
    Record<number, NotificationControlValues>
  >({});
  const [loading, setLoading] = useState<boolean>(false);
  const notificationSwitchFields = [
    {
      name: "broadcast" as const,
      allowedKey: "broadcastAllowed" as const,
      Icon: MailOutlineIcon,
    },
    {
      name: "inApp" as const,
      allowedKey: "inAppAllowed" as const,
      Icon: ChatBubbleOutlineIcon,
    },
    {
      name: "push" as const,
      allowedKey: "pushAllowed" as const,
      Icon: NotificationsNoneIcon,
    },
  ];
  const branding = useSelector(brandingInfo);
  const themeColors =
    branding?.attributes?.themeId === 100
      ? branding?.attributes?.customTheme
      : (themes as any)[branding?.attributes?.themeId || 3];
  const switchStyles = {
    width: SWITCH_WIDTH,
    height: 20,
    p: 0,
    "& .MuiSwitch-switchBase": {
      p: "2px",
      color: "#fff",
      transition: "transform 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms",
      "&.Mui-checked": {
        transform: `translateX(${SWITCH_CHECKED_TRANSLATE_X}px)`,
        "& + .MuiSwitch-track": {
          bgcolor: themeColors?.primary,
          opacity: 1,
        },
      },
    },
    "& .MuiSwitch-thumb": {
      width: SWITCH_THUMB_SIZE,
      height: SWITCH_THUMB_SIZE,
      boxShadow: "none",
      background: "#fff",
    },
    "& .MuiSwitch-track": {
      borderRadius: "21px",
      bgcolor: themeColors?.button,
      opacity: 1,
    },
  };
  const {
    data: eventTypesData,
    isFetching: eventTypesFetching,
    error: eventTypesError,
    refetch: eventTypesRefetch,
  } = useGetNotificationEventTypesService({
    pageNumber: pageNo,
    size: EVENT_TYPES_PAGE_SIZE,
    title: debouncedSearchTitle,
    active: appliedActive,
  });
  const {
    data: childEventTypesData,
    isFetching: childEventTypesFetching,
    refetch: childEventTypesRefetch,
  } = useGetEventTypesByParentIdService(expandedParentId ?? undefined);
  const {
    data: subEventTypesData,
    isFetching: subEventTypesFetching,
    refetch: subEventTypesRefetch,
  } = useGetNotificationSubEventTypesService({
    pageNumber: 1,
    size: 50,
    eventTypeId: expandedChildId ?? undefined,
  });
  const eventTypesPageData = eventTypesData?.data as
    | {
        content?: NotificationEventTypeResponse[];
        totalPages?: number;
        totalElements?: number;
      }
    | undefined;
  const eventTypes = (eventTypesPageData?.content ||
    []) as NotificationEventTypeResponse[];
  const eventTypeTotalPages =
    eventTypesPageData?.totalPages ??
    eventTypesData?.totalPages ??
    (eventTypesPageData?.totalElements
      ? Math.ceil(eventTypesPageData.totalElements / EVENT_TYPES_PAGE_SIZE)
      : 0);
  const childEventTypesRaw = childEventTypesData?.data;
  const childEventTypes = (
    Array.isArray(childEventTypesRaw)
      ? childEventTypesRaw
      : childEventTypesRaw?.content || []
  ) as NotificationEventTypeResponse[];
  const subEventTypes = (subEventTypesData?.data?.content ||
    []) as NotificationEventSubTypeResponse[];
  const showEventTypesNoRecord =
    !eventTypesFetching &&
    (!!eventTypesError ||
      eventTypesData?.success === false ||
      !eventTypes.length);
  useEffect(() => {
    eventTypesRefetch();
  }, [pageNo, debouncedSearchTitle, appliedActive, eventTypesRefetch]);
  useEffect(() => {
    if (expandedParentId) {
      childEventTypesRefetch();
    }
  }, [expandedParentId, childEventTypesRefetch]);
  useEffect(() => {
    setChildControlStates({});
  }, [expandedParentId]);
  useEffect(() => {
    if (!childEventTypesData) return;
    const raw = childEventTypesData.data;
    const items = (
      Array.isArray(raw) ? raw : raw?.content || []
    ) as NotificationEventTypeResponse[];
    if (!items.length) return;
    setChildControlStates((prev) => {
      const next = { ...prev };
      items.forEach((child) => {
        const control = child.notificationControlResponse;
        const hasApiControl =
          control &&
          (control.inApp !== undefined ||
            control.push !== undefined ||
            control.broadcast !== undefined);
        const hasTopLevelControl =
          child.inApp !== undefined ||
          child.push !== undefined ||
          child.broadcast !== undefined;
        if (hasApiControl || hasTopLevelControl) {
          next[child.id] = extractNotificationControlValues(child);
        }
      });
      return next;
    });
  }, [childEventTypesData]);
  useEffect(() => {
    if (expandedChildId) {
      subEventTypesRefetch();
    }
  }, [expandedChildId, subEventTypesRefetch]);
  useEffect(() => {
    if (!subEventTypesData || !expandedChildId) return;
    const items = (subEventTypesData?.data?.content ||
      []) as NotificationEventSubTypeResponse[];
    if (!items.length) return;
    const getRelevant = (
      allowedKey: "inAppAllowed" | "pushAllowed" | "broadcastAllowed",
    ) => items.filter((item) => item.active !== false && item[allowedKey] !== false);
    const inAppItems = getRelevant("inAppAllowed");
    const pushItems = getRelevant("pushAllowed");
    const broadcastItems = getRelevant("broadcastAllowed");
    setChildControlStates((prev) => ({
      ...prev,
      [expandedChildId]: {
        inApp:
          inAppItems.length > 0 &&
          inAppItems.every(
            (item) => extractNotificationControlValues(item).inApp,
          ),
        push:
          pushItems.length > 0 &&
          pushItems.every(
            (item) => extractNotificationControlValues(item).push,
          ),
        broadcast:
          broadcastItems.length > 0 &&
          broadcastItems.every(
            (item) => extractNotificationControlValues(item).broadcast,
          ),
      },
    }));
  }, [subEventTypesData, expandedChildId]);
  const resetExpandedState = () => {
    setExpandedParentId(null);
    setExpandedChildId(null);
  };
  const toggleNotificationSwitches = (
    mappingId: number,
    values: { inApp: boolean; push: boolean; broadcast: boolean },
    changedField: "inApp" | "push" | "broadcast",
    newValue: boolean,
  ) => {
    AccountSettingsProfileService.controlNotification({
      mappingId,
      inApp: changedField === "inApp" ? newValue : values.inApp,
      push: changedField === "push" ? newValue : values.push,
      broadcast: changedField === "broadcast" ? newValue : values.broadcast,
    })
      .then((res: ControlNotificationResponse) => {
        if (res.success) {
          subEventTypesRefetch();
        } else {
          enqueueSnackbar(
            <AlertMessages text={res.message || "Something went wrong"} />,
            { variant: "error" },
          );
        }
      })
      .catch((err: any) => {
        console.error("===", err);
      })
      .finally(() => setLoading(false));
  };
  const getChildControlValues = (
    child: NotificationEventTypeResponse,
  ): NotificationControlValues =>
    childControlStates[child.id] ?? extractNotificationControlValues(child);
  const toggleChildNotificationSwitches = (
    eventTypeId: number,
    values: NotificationControlValues,
    changedField: "inApp" | "push" | "broadcast",
    newValue: boolean,
  ) => {
    const updatedValues: NotificationControlValues = {
      inApp: changedField === "inApp" ? newValue : values.inApp,
      push: changedField === "push" ? newValue : values.push,
      broadcast: changedField === "broadcast" ? newValue : values.broadcast,
    };
    setChildControlStates((prev) => ({
      ...prev,
      [eventTypeId]: updatedValues,
    }));
    AccountSettingsProfileService.controlMultiNotification({
      eventTypeId,
      ...updatedValues,
    })
      .then((res: ControlMultiNotificationResponse) => {
        if (res.success) {
          const responseData = Array.isArray(res.data) ? res.data[0] : res.data;
          if (responseData) {
            setChildControlStates((prev) => ({
              ...prev,
              [eventTypeId]: {
                inApp: responseData.inApp ?? updatedValues.inApp,
                push: responseData.push ?? updatedValues.push,
                broadcast: responseData.broadcast ?? updatedValues.broadcast,
              },
            }));
          }
          childEventTypesRefetch();
          if (expandedChildId === eventTypeId) {
            subEventTypesRefetch();
          }
        } else {
          setChildControlStates((prev) => ({
            ...prev,
            [eventTypeId]: values,
          }));
          enqueueSnackbar(
            <AlertMessages text={res.message || "Something went wrong"} />,
            { variant: "error" },
          );
        }
      })
      .catch((err: any) => {
        setChildControlStates((prev) => ({
          ...prev,
          [eventTypeId]: values,
        }));
        console.error("===", err);
      })
      .finally(() => setLoading(false));
  };
  const handleSearchClick = () => {
    const trimmedSearch = searchEventType.trim();
    setDebouncedSearchTitle(trimmedSearch);
    setAppliedActive(statusFilter?.value ?? true);
    setPageNo(1);
    resetExpandedState();
  };
  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchEventType(event.target.value.trim());
  };
  const clearSearchEventType = () => {
    setSearchEventType("");
    setDebouncedSearchTitle("");
    setStatusFilter(statusOptions[0]);
    setAppliedActive(true);
    setPageNo(1);
    resetExpandedState();
    setSearchInputKey((key) => key + 1);
  };
  const handleParentAccordionChange = (
    parentId: number,
    isExpanded: boolean,
  ) => {
    if (isExpanded) {
      setExpandedParentId(parentId);
      setExpandedChildId(null);
    } else {
      setExpandedParentId(null);
      setExpandedChildId(null);
    }
  };
  const handleChildAccordionChange = (childId: number, isExpanded: boolean) => {
    if (isExpanded) {
      setExpandedChildId(childId);
    } else {
      setExpandedChildId(null);
    }
  };
  const renderNotificationSwitch = (
    name: "inApp" | "push" | "broadcast",
    checked: boolean,
    values: { inApp: boolean; push: boolean; broadcast: boolean },
    onToggle: (
      field: "inApp" | "push" | "broadcast",
      newValue: boolean,
    ) => void,
  ) => (
    <FormControlLabel
      key={name}
      control={
        <Switch
          checked={checked}
          onChange={(e) => {
            e.stopPropagation();
            setLoading(true);
            onToggle(name, e.target.checked);
          }}
          onClick={(e) => e.stopPropagation()}
          sx={switchStyles}
        />
      }
      label=""
      sx={{ m: 0, width: SWITCH_COLUMN_WIDTH, justifyContent: "center" }}
      onClick={(e) => e.stopPropagation()}
    />
  );
  const renderNotificationSwitchColumns = (
    item: {
      active?: boolean;
      broadcastAllowed?: boolean;
      inAppAllowed?: boolean;
      pushAllowed?: boolean;
    },
    values: NotificationControlValues,
    onToggle: (
      field: "inApp" | "push" | "broadcast",
      newValue: boolean,
    ) => void,
    showSwitches = true,
    showIcons = true,
  ) => {
    const isDisabled = item.active === false;
    return (
      <Box
        sx={{
          display: "flex",
          flexShrink: 0,
          width: NOTIFICATION_COLUMNS_WIDTH,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {notificationSwitchFields.map(({ name, allowedKey, Icon }) => {
          const isAllowed = item[allowedKey] !== false;
          return (
            <Box
              key={name}
              sx={{
                width: SWITCH_COLUMN_WIDTH,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "flex-start",
                gap: 0.5,
                flexShrink: 0,
                minHeight: showIcons ? 44 : 20,
              }}
            >
              {isAllowed ? (
                <>
                  {showIcons ? (
                    <Tooltip title={t(name)}>
                      <Icon
                        sx={{
                          fontSize: 19,
                          color: isDisabled ? "#B0B0B0" : "#4A4A4A",
                          cursor: "text",
                        }}
                      />
                    </Tooltip>
                  ) : (
                    <Box sx={{ height: 20 }} />
                  )}
                  {!isDisabled && showSwitches ? (
                    renderNotificationSwitch(
                      name,
                      values[name],
                      values,
                      onToggle,
                    )
                  ) : (
                    <Box sx={{ height: 20 }} />
                  )}
                </>
              ) : (
                <Box sx={{ width: "100%", minHeight: showIcons ? 44 : 20 }} />
              )}
            </Box>
          );
        })}
      </Box>
    );
  };
  const renderNoRecordFound = () => (
    <Typography
      className="font-Saira font-[500] text-[15px] italic"
      sx={{ color: themeColors?.primary }}
    >
      {t("noRecordFound")}
    </Typography>
  );
  const renderGrandchildRow = (item: NotificationEventSubTypeResponse) => {
    const control = item.notificationControlResponse || {};
    const mappingId = control.mappingId || item.mappingId || 0;
    const values = extractNotificationControlValues(item);
    const isDisabled = !item.active;
    return (
      <Box
        key={item.id}
        sx={{
          display: "flex",
          alignItems: "center",
          py: 1.5,
          borderBottom: "1px solid #E5E5E5",
        }}
      >
        <Typography
          className="font-Saira"
          sx={{
            flex: 1,
            minWidth: 0,
            fontWeight: 600,
            fontSize: 13,
            color: isDisabled ? "#B0B0B0" : "#4A4A4A",
            display: "flex",
            alignItems: "center",
            gap: 1,
            pl: 1,
          }}
        >
          <Box
            component="span"
            sx={{ color: "#B0B0B0", fontSize: 8, lineHeight: 1 }}
          >
            ●
          </Box>
          {item.title ? t(item.title) : ""}
        </Typography>
        {renderNotificationSwitchColumns(
          item,
          values,
          (field, newValue) =>
            toggleNotificationSwitches(mappingId, values, field, newValue),
          !!mappingId && !isDisabled,
          false,
        )}
      </Box>
    );
  };
  const renderChildGrandchildren = (child: NotificationEventTypeResponse) => {
    if (expandedChildId !== child.id) return null;
    if (subEventTypesFetching) {
      return (
        <Box sx={{ py: 2, display: "flex", justifyContent: "center" }}>
          <Loader />
        </Box>
      );
    }
    if (!subEventTypes.length) {
      return <Box sx={{ px: 2, py: 2 }}>{renderNoRecordFound()}</Box>;
    }
    return subEventTypes.map(renderGrandchildRow);
  };
  const renderChildAccordions = () => {
    if (childEventTypesFetching) {
      return (
        <Box sx={{ py: 2, display: "flex", justifyContent: "center" }}>
          <Loader />
        </Box>
      );
    }
    if (!childEventTypes.length) {
      return <Box sx={{ px: 2, py: 2.5 }}>{renderNoRecordFound()}</Box>;
    }
    return childEventTypes.map((child) => {
      const isChildExpanded = expandedChildId === child.id;
      const childValues = getChildControlValues(child);
      const childSwitchItem =
        isChildExpanded && !subEventTypesFetching
          ? { ...child, ...getAllowedFromSubEvents(subEventTypes) }
          : child;
      return (
        <Accordion
          key={child.id}
          expanded={isChildExpanded}
          onChange={(_, isExpanded) =>
            handleChildAccordionChange(child.id, isExpanded)
          }
          sx={{
            boxShadow: "none",
            "&:before": { display: "none" },
            bgcolor: "transparent",
            mb: 0.5,
          }}
          disableGutters
        >
          <AccordionSummary
            expandIcon={<AccordionExpandIcon expanded={isChildExpanded} />}
            aria-controls={`notification-child-${child.id}`}
            id={`notification-child-header-${child.id}`}
            sx={{
              bgcolor: "#F3F3F3",
              borderRadius: "4px",
              px: 2,
              minHeight: 44,
              flexDirection: "row-reverse",
              ...accordionExpandIconSx,
              "& .MuiAccordionSummary-content": {
                margin: "8px 0 !important",
                alignItems: "center",
                width: "100%",
              },
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                width: "100%",
                gap: 1,
              }}
            >
              <Typography
                className="font-Saira"
                sx={{
                  flex: 1,
                  fontWeight: 600,
                  fontSize: 13,
                  color: "#4A4A4A",
                  textAlign: "left",
                }}
              >
                {t(child.title)}
              </Typography>
              {renderNotificationSwitchColumns(
                childSwitchItem,
                childValues,
                (field, newValue) =>
                  toggleChildNotificationSwitches(
                    child.id,
                    childValues,
                    field,
                    newValue,
                  ),
              )}
            </Box>
          </AccordionSummary>
          <AccordionDetails
            sx={{
              p: 0,
              pl: `${CHILD_ACCORDION_EXPAND_OFFSET}px`,
              pr: 2,
              bgcolor: "#fff",
            }}
          >
            {renderChildGrandchildren(child)}
          </AccordionDetails>
        </Accordion>
      );
    });
  };
  const renderParentAccordionDetails = (
    parent: NotificationEventTypeResponse,
  ) => {
    if (expandedParentId !== parent.id) return null;
    return (
      <Box
        sx={{
          pl: 2,
          pr: 1,
          py: 1,
          borderLeft: "1px solid #E5E5E5",
          ml: 2,
        }}
      >
        {renderChildAccordions()}
      </Box>
    );
  };
  const paginationSx = {
    mt: 2,
    "& .MuiPaginationItem-root": {
      fontFamily: "Saira, sans-serif",
      fontWeight: 600,
      color: "#4A4A4A",
    },
    "& .MuiPagination-ul": { justifyContent: "center" },
    "& .MuiPaginationItem-root.Mui-selected": {
      bgcolor: themeColors?.primary,
      color: "#fff",
      "&:hover": { bgcolor: themeColors?.primary },
    },
  };
  const accordionSx = {
    boxShadow: "none",
    borderRadius: "8px",
    mb: 1,
    "&:before": { display: "none" },
    overflow: "hidden",
    "&.MuiAccordion-root": { borderRadius: "8px" },
  };
  const parentAccordionSummarySx = {
    bgcolor: themeColors?.button,
    borderRadius: "8px",
    px: 2,
    ...accordionExpandIconSx,
    "& .MuiAccordionSummary-content": {
      margin: "12px 0 !important",
      alignItems: "center",
    },
  };
  return (
    <>
      <ComponentHeaderTwo
        showBtn={false}
        showSaveBtn={false}
        heading={t("accountSettings")}
        icon={<Accounts height={28} width={28} />}
        btnText="back"
        btnType="back"
      />
      <div className="flex">
        <AccountSettingsTabs selectedIndex={2} />
        <SimpleModal
          visable={true}
          title={t("notificationSettings")}
          isButtonVisable={false}
          notVisableBackbtn={true}
          content={
            <Box sx={{ "& .MuiBackdrop-root": { zIndex: 1 } }}>
              {loading || eventTypesFetching ? <Loader /> : null}
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Grid container spacing={1.5} alignItems="flex-end">
                    <Grid item xs={12} sm={6} md={4}>
                      <label className="font-Saira font-[600] text-[13px] text-[#343434] text-left block">{t("notificationEventType")}</label>
                      <StyledInput
                        key={searchInputKey}
                        fullWidth
                        hideBtn
                        placeholder="notificationEventType"
                        handleSearchClick={handleSearchClick}
                        handleChange={handleInputChange}
                        value={searchEventType}
                        clearSearch={clearSearchEventType}
                      />
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      md={3}
                      sx={{
                        "& .dropdown": { display: "none !important" },
                        "& .commonSelect [class*='-control']": {
                          minHeight: `${FILTER_CONTROL_HEIGHT}px`,
                          height: `${FILTER_CONTROL_HEIGHT}px`,
                        },
                      }}
                    >
                      <DynamicSelect
                        label={t("status")}
                        options={statusOptions}
                        value={statusFilter}
                        onChange={(option) => setStatusFilter(option)}
                        isClearable={false}
                        ddWidth="100%"
                        ddHeight={`${FILTER_CONTROL_HEIGHT}px`}
                      />
                    </Grid>
                    <Grid item xs={12} sm={12} md={4}>
                      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                        <Box>
                          {filterFieldLabelSpacer}
                          <Button
                            variant="contained"
                            onClick={handleSearchClick}
                            sx={{
                              boxShadow: "none",
                              background: themeColors?.primary,
                              height: FILTER_CONTROL_HEIGHT,
                              px: 4,
                            }}
                            className="text-white font-Saira font-[600] text-[13px]"
                          >
                            {t("submit")}
                          </Button>
                        </Box>
                        {searchEventType ? (
                          <Box>
                            {filterFieldLabelSpacer}
                            <Button
                              variant="contained"
                              onClick={clearSearchEventType}
                              sx={{
                                boxShadow: "none",
                                color: themeColors?.primary,
                                bgcolor: "#d8d1e0",
                                height: FILTER_CONTROL_HEIGHT,
                                px: 4,
                              }}
                              className="font-Saira font-[600] text-[13px]"
                            >
                              {t("clear")}
                            </Button>
                          </Box>
                        ) : null}
                      </Box>
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item xs={12} className="pt-[8px]">
                  {showEventTypesNoRecord ? (
                    <Box sx={{ py: 2 }}>{renderNoRecordFound()}</Box>
                  ) : (
                    <>
                      {eventTypes.map((item) => {
                        const isParentExpanded = expandedParentId === item.id;
                        return (
                          <Accordion
                            key={item.id}
                            expanded={isParentExpanded}
                            onChange={(_, isExpanded) =>
                              handleParentAccordionChange(item.id, isExpanded)
                            }
                            sx={accordionSx}
                            disableGutters
                          >
                            <AccordionSummary
                              expandIcon={
                                <AccordionExpandIcon
                                  expanded={isParentExpanded}
                                />
                              }
                              aria-controls={`notification-parent-${item.id}`}
                              id={`notification-parent-header-${item.id}`}
                              sx={{
                                ...parentAccordionSummarySx,
                                flexDirection: "row-reverse",
                              }}
                            >
                              <Typography
                                className="font-Saira"
                                sx={{
                                  flex: 1,
                                  fontWeight: 700,
                                  fontSize: 13,
                                  color: themeColors?.primary,
                                  textAlign: "left",
                                }}
                              >
                                {t(item.title)}
                              </Typography>
                            </AccordionSummary>
                            <AccordionDetails
                              sx={{
                                p: "0 10px 10px 10px",
                                bgcolor: "#fff",
                              }}
                            >
                              {renderParentAccordionDetails(item)}
                            </AccordionDetails>
                          </Accordion>
                        );
                      })}
                      {eventTypeTotalPages > 1 && (
                        <Pagination
                          count={eventTypeTotalPages}
                          page={pageNo}
                          onChange={(_, page) => {
                            setPageNo(page);
                            resetExpandedState();
                          }}
                          shape="rounded"
                          sx={paginationSx}
                        />
                      )}
                    </>
                  )}
                </Grid>
              </Grid>
            </Box>
          }
        />
      </div>
    </>
  );
}
